import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ApiError, postJson } from './api';

const PASSWORD_MIN_LENGTH = 15;
const PASSWORD_MAX_LENGTH = 128;

type Mode = 'login' | 'register' | 'forgot' | 'resend';

export function EmailAuthPage({ kind }: { kind: 'login' | 'verify' | 'reset' }) {
  if (kind === 'verify') return <VerifyEmailPage />;
  if (kind === 'reset') return <ResetPasswordPage />;
  return <LoginPage />;
}

function LoginPage() {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  function switchMode(next: Mode) {
    setMode(next);
    setPassword('');
    setNotice('');
    setError('');
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setNotice('');
    setError('');
    try {
      if (mode === 'forgot') {
        await postJson('/api/auth/forgot-password', { email });
        setNotice('如果该邮箱已注册，我们会发送密码重置邮件。');
        return;
      }
      if (mode === 'resend') {
        await postJson('/api/auth/resend-verification', { email });
        setNotice('如果账号尚未验证，我们会发送验证邮件。');
        return;
      }
      if (mode === 'register') {
        await postJson('/api/auth/register', { email, password });
        setPassword('');
        setNotice('如果该邮箱可用于注册，我们会发送验证邮件。请检查收件箱或垃圾邮件。');
        return;
      }
      await postJson('/api/auth/login', { email, password });
      window.location.assign('/app');
    } catch (value) {
      if (value instanceof ApiError && value.code === 'EMAIL_NOT_VERIFIED') {
        setMode('resend');
        setNotice('这个账号尚未完成邮箱验证。');
      } else {
        setError(errorMessage(value));
      }
    } finally {
      setBusy(false);
    }
  }

  const needsPassword = mode === 'login' || mode === 'register';
  const title = mode === 'register' ? '创建账号' : mode === 'forgot' ? '忘记密码' : mode === 'resend' ? '验证邮箱' : '邮箱登录';
  const action = mode === 'register' ? '创建账号' : mode === 'forgot' ? '发送重置邮件' : mode === 'resend' ? '重新发送验证邮件' : '登录';

  return <AuthFrame title={title}>
    <form onSubmit={submit} className="stack">
      <label>邮箱<input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" required /></label>
      {needsPassword && <label>密码<input
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        type="password"
        minLength={mode === 'register' ? PASSWORD_MIN_LENGTH : 1}
        maxLength={PASSWORD_MAX_LENGTH}
        autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
        required
      /></label>}
      {mode === 'register' && <span className="muted">至少 15 个字符，最多 128 个字符；支持空格、符号和 Unicode，可直接使用密码管理器生成的密码或长口令。</span>}
      {notice && <div className="notice" role="status">{notice}</div>}
      {error && <div className="form-error" role="alert">{error}</div>}
      <button className="button primary full" disabled={busy}>{busy ? '处理中…' : action}</button>
    </form>
    <p className="auth-note">
      {mode === 'login' ? <>
        还没有账号？ <button type="button" className="text-button" onClick={() => switchMode('register')}>立即注册</button>
        {' · '}<button type="button" className="text-button" onClick={() => switchMode('forgot')}>忘记密码</button>
        {' · '}<button type="button" className="text-button" onClick={() => switchMode('resend')}>重发验证</button>
      </> : <button type="button" className="text-button" onClick={() => switchMode('login')}>返回邮箱登录</button>}
    </p>
    <p className="auth-note">登录或注册即表示你同意 <Link to="/terms">服务条款</Link> 与 <Link to="/privacy">隐私说明</Link>。</p>
  </AuthFrame>;
}

function useOneTimeUrlToken(): string {
  const location = useLocation();
  const [token] = useState(() => new URLSearchParams(location.search).get('token') || '');
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has('token')) {
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.hash);
    }
  }, []);
  return token;
}

function VerifyEmailPage() {
  const token = useOneTimeUrlToken();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(token ? '' : '验证链接不完整。');
  async function verify() {
    setBusy(true);
    setError('');
    try {
      await postJson('/api/auth/verify-email', { token });
      setDone(true);
    } catch (value) {
      setError(errorMessage(value));
    } finally {
      setBusy(false);
    }
  }
  return <AuthFrame title={done ? '邮箱验证成功' : '确认验证邮箱'}>
    <p>{done ? '现在可以使用邮箱和密码登录。' : '验证链接只能使用一次，并会在有效期后失效。'}</p>
    {error && <div className="form-error" role="alert">{error}</div>}
    {done ? <Link className="button primary full" to="/login">返回登录</Link> : <button className="button primary full" onClick={verify} disabled={!token || busy}>{busy ? '验证中…' : '验证邮箱'}</button>}
  </AuthFrame>;
}

function ResetPasswordPage() {
  const token = useOneTimeUrlToken();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(token ? '' : '重置链接不完整。');
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirmation) {
      setError('两次输入的密码不一致。');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await postJson('/api/auth/reset-password', { token, password });
      setDone(true);
    } catch (value) {
      setError(errorMessage(value));
    } finally {
      setBusy(false);
    }
  }
  return <AuthFrame title={done ? '密码已重置' : '设置新密码'}>
    <p>{done ? '旧会话和其他重置链接已经失效。' : '新密码至少 15 个字符，支持空格、符号和 Unicode。'}</p>
    {done ? <Link className="button primary full" to="/login">使用新密码登录</Link> : <form className="stack" onSubmit={submit}>
      <label>新密码<input value={password} onChange={(event) => setPassword(event.target.value)} type="password" minLength={PASSWORD_MIN_LENGTH} maxLength={PASSWORD_MAX_LENGTH} autoComplete="new-password" required /></label>
      <label>再次输入<input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} type="password" minLength={PASSWORD_MIN_LENGTH} maxLength={PASSWORD_MAX_LENGTH} autoComplete="new-password" required /></label>
      {error && <div className="form-error" role="alert">{error}</div>}
      <button className="button primary full" disabled={!token || busy}>{busy ? '重置中…' : '重置密码'}</button>
    </form>}
  </AuthFrame>;
}

function AuthFrame({ title, children }: { title: string; children: ReactNode }) {
  return <div className="auth-page">
    <Link to="/" className="auth-brand"><strong>FeedDive</strong></Link>
    <div className="auth-card"><h1>{title}</h1>{children}</div>
  </div>;
}

function errorMessage(value: unknown): string {
  return value instanceof Error ? value.message : '请求失败，请稍后重试。';
}
